// IndexedDB persistence layer for offline assessments (Fase 2).
//
// Two object stores:
//   - offline_assessments: one snapshot per evaluation_line id (structure + data +
//     rules inputs + user profile). Everything a view needs to render with no network.
//   - sync_queue: ordered pending changes (scores / comments) to upload on sync.
//
// This module is framework-agnostic (no Vue/Vuex) so it can be unit-tested and reused.
import { openDB } from 'idb'

const DB_NAME = 'pqi-offline'
const DB_VERSION = 1

export const STORE_SNAPSHOTS = 'offline_assessments'
export const STORE_QUEUE = 'sync_queue'

let dbPromise = null

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_SNAPSHOTS)) {
          // keyPath = evaluation_line id
          db.createObjectStore(STORE_SNAPSHOTS, { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains(STORE_QUEUE)) {
          const queue = db.createObjectStore(STORE_QUEUE, { keyPath: 'seq', autoIncrement: true })
          // query/clear a single assessment's pending changes
          queue.createIndex('by_line', 'lineId')
        }
      },
    })
  }

  return dbPromise
}

// ---- Snapshots -------------------------------------------------------------

/**
 * Persist (or overwrite) the offline snapshot for one assessment.
 * @param {object} snapshot must include `id` (the evaluation_line id).
 */
export async function saveSnapshot(snapshot) {
  if (!snapshot || snapshot.id == null) throw new Error('saveSnapshot: snapshot.id is required')
  const db = await getDB()

  return db.put(STORE_SNAPSHOTS, snapshot)
}

export async function getSnapshot(lineId) {
  const db = await getDB()

  return db.get(STORE_SNAPSHOTS, lineId)
}

export async function listSnapshots() {
  const db = await getDB()

  return db.getAll(STORE_SNAPSHOTS)
}

/**
 * Drop the local copy of an assessment: its snapshot and every queued change.
 * NOTE: this does NOT release the backend lock (no release EP yet) — the line
 * stays flagged is_downloaded_offline until sync or an admin frees it.
 */
export async function clearAssessment(lineId) {
  const db = await getDB()
  const tx = db.transaction([STORE_SNAPSHOTS, STORE_QUEUE], 'readwrite')
  await tx.objectStore(STORE_SNAPSHOTS).delete(lineId)

  const index = tx.objectStore(STORE_QUEUE).index('by_line')
  let cursor = await index.openCursor(lineId)
  while (cursor) {
    await cursor.delete()
    cursor = await cursor.continue()
  }
  await tx.done
}

// ---- Sync queue ------------------------------------------------------------

/**
 * Append a pending change. `seq` (autoIncrement) is the store key / insertion
 * order — not a semantic timestamp. Prefer `upsertQueueByKey` for keyed deltas.
 * @param {object} change { lineId, type: 'score'|'comment', ... }
 */
export async function queueChange(change) {
  if (!change || change.lineId == null) throw new Error('queueChange: change.lineId is required')
  const db = await getDB()

  return db.add(STORE_QUEUE, change)
}

/**
 * Pending changes, ordered by insertion. Pass a lineId to scope to one assessment.
 */
export async function getQueue(lineId = null) {
  const db = await getDB()
  if (lineId == null) return db.getAll(STORE_QUEUE)

  return db.getAllFromIndex(STORE_QUEUE, 'by_line', lineId)
}

/**
 * Upsert a change keyed by `key` within a line: if an entry with the same
 * (lineId, key) exists it is replaced (keeping its seq/order); otherwise appended.
 * Keeps the queue at one net entry per target (e.g. one per indicator_principle).
 */
export async function upsertQueueByKey(lineId, key, change) {
  const db = await getDB()
  const tx = db.transaction(STORE_QUEUE, 'readwrite')
  const store = tx.objectStore(STORE_QUEUE)
  let existing = null
  let cursor = await store.index('by_line').openCursor(lineId)
  while (cursor) {
    if (cursor.value.key === key) { existing = cursor.value; break }
    cursor = await cursor.continue()
  }
  const record = { ...change, key, lineId }
  if (existing) record.seq = existing.seq
  await store.put(record)
  await tx.done
}

/** Remove the entry with (lineId, key), if any. Used to cancel a delta that
 * reverted to the baseline value (net no-op). */
export async function removeQueueByKey(lineId, key) {
  const db = await getDB()
  const tx = db.transaction(STORE_QUEUE, 'readwrite')
  const index = tx.objectStore(STORE_QUEUE).index('by_line')
  let cursor = await index.openCursor(lineId)
  while (cursor) {
    if (cursor.value.key === key) await cursor.delete()
    cursor = await cursor.continue()
  }
  await tx.done
}

export async function clearQueue(lineId) {
  const db = await getDB()
  const tx = db.transaction(STORE_QUEUE, 'readwrite')
  const index = tx.objectStore(STORE_QUEUE).index('by_line')
  let cursor = await index.openCursor(lineId)
  while (cursor) {
    await cursor.delete()
    cursor = await cursor.continue()
  }
  await tx.done
}

export default {
  saveSnapshot,
  getSnapshot,
  listSnapshots,
  clearAssessment,
  queueChange,
  upsertQueueByKey,
  removeQueueByKey,
  getQueue,
  clearQueue,
}
